import type { TFunction } from 'react-i18next'
import { v4 as uuidv4 } from 'uuid'
import { ARGENTINA, BRAZIL } from 'constants/contacts'
import type { ContactEntityType, GovernmentId, GovernmentIdType } from 'types/contacts'
import { MAX_GOVERNMENT_IDS } from './constants'

interface GovernmentIdConfig {
  pattern: RegExp
  countryCode: string
  formatErrorKey: string
  length: number
}

const GOVERNMENT_ID_CONFIG: Record<GovernmentIdType, GovernmentIdConfig> = {
  CUIL: {
    pattern: /^\d{11}$/,
    countryCode: ARGENTINA,
    formatErrorKey: 'contacts.governmentIds.digitsFormatError',
    length: 11,
  },
  CUIT: {
    pattern: /^\d{11}$/,
    countryCode: ARGENTINA,
    formatErrorKey: 'contacts.governmentIds.digitsFormatError',
    length: 11,
  },
  CPF: {
    pattern: /^\d{11}$/,
    countryCode: BRAZIL,
    formatErrorKey: 'contacts.governmentIds.digitsFormatError',
    length: 11,
  },
  CNPJ: {
    pattern: /^[A-Z0-9]{14}$/i,
    countryCode: BRAZIL,
    formatErrorKey: 'contacts.governmentIds.charactersFormatError',
    length: 14,
  },
}

const GOVERNMENT_ID_REGISTRY: Record<string, Record<ContactEntityType, GovernmentIdType>> = {
  [ARGENTINA]: {
    COMPANY: 'CUIT',
    INDIVIDUAL: 'CUIL',
  },
  [BRAZIL]: {
    COMPANY: 'CNPJ',
    INDIVIDUAL: 'CPF',
  },
}

export const createEmptyGovernmentId = (): GovernmentId => ({
  type: undefined,
  number: '',
  issuingCountry: '',
  rowId: uuidv4(),
})

export const getIdTypesForCountry = (countryCode: string): GovernmentIdType[] =>
  (Object.keys(GOVERNMENT_ID_CONFIG) as GovernmentIdType[]).filter(
    type => GOVERNMENT_ID_CONFIG[type].countryCode === countryCode
  )

export const getIdTypeForCountryAndEntity = (
  countryCode: string,
  entityType: ContactEntityType | undefined
): GovernmentIdType | undefined =>
  entityType ? GOVERNMENT_ID_REGISTRY[countryCode]?.[entityType] : undefined

export const getGovernmentIdsForEntityType = (
  governmentIds: GovernmentId[],
  entityType: ContactEntityType
): GovernmentId[] =>
  governmentIds.map(governmentId =>
    governmentId.issuingCountry
      ? {
          ...governmentId,
          type: getIdTypeForCountryAndEntity(governmentId.issuingCountry, entityType),
          number: '',
        }
      : governmentId
  )

export const withRequiredGovernmentId = (
  governmentIds: GovernmentId[],
  requiredCountry: string | undefined,
  entityType: ContactEntityType | undefined
): GovernmentId[] => {
  const hasRequiredCountry = governmentIds.some(
    governmentId => governmentId.issuingCountry === requiredCountry
  )

  if (!requiredCountry || hasRequiredCountry || governmentIds.length >= MAX_GOVERNMENT_IDS) {
    return governmentIds
  }

  return [
    ...governmentIds,
    {
      ...createEmptyGovernmentId(),
      issuingCountry: requiredCountry,
      type: getIdTypeForCountryAndEntity(requiredCountry, entityType),
    },
  ]
}

// Unknown types pass, as there's no format to check them against
export const isValidGovernmentIdNumber = (
  type: GovernmentIdType | undefined,
  value: string
): boolean => {
  const config = type && GOVERNMENT_ID_CONFIG[type]

  if (!config) return true

  return config.pattern.test(value)
}

export const getGovernmentIdFormatErrorMessage = (
  type: GovernmentIdType | undefined,
  t: TFunction<'translation', undefined>
): string => {
  const config = type && GOVERNMENT_ID_CONFIG[type]

  if (!config) return t('contacts.governmentIds.invalidFormatError')

  return t(config.formatErrorKey, { type, length: config.length })
}
